import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';

const STORAGE_KEY_MOBILE = 'product-grid-view-mobile';
const STORAGE_KEY_DESKTOP = 'product-grid-view-desktop';

const MOBILE_VIEWS = ['view-by-2', 'view-by-3'];
const DESKTOP_VIEWS = ['view-by-6', 'view-by-3'];

function updateLabel(element, value) {
  if (!element) return;
  const num = value.replace('view-by-', '');
  element.textContent = `VIEW BY ${num}`;
}

function getStoredLayout(viewport) {
  const key = viewport === 'desktop' ? STORAGE_KEY_DESKTOP : STORAGE_KEY_MOBILE;
  return sessionStorage.getItem(key) || (viewport === 'desktop' ? 'view-by-6' : 'view-by-2');
}

function saveLayout(viewport, value) {
  const key = viewport === 'desktop' ? STORAGE_KEY_DESKTOP : STORAGE_KEY_MOBILE;
  sessionStorage.setItem(key, value);
}

function toggleView(currentView, viewport) {
  if (viewport === 'mobile') {
    return currentView === 'view-by-2' ? 'view-by-3' : 'view-by-2';
  }
  return currentView === 'view-by-6' ? 'view-by-3' : 'view-by-6';
}

/** Label shows what you'll GET when you click (the target). So "VIEW BY 3" means click to get 3 cols. */
function getLabelToDisplay(currentView, viewport) {
  return toggleView(currentView, viewport);
}

/** All main-collection product grids (e.g. primary + secondary on the same template). */
function getCollectionPageGrids() {
  return document.querySelectorAll(
    'results-list.collection-page__main ul.product-grid[data-testid="product-grid"]'
  );
}

/**
 * Bind view-by controls: one bar can drive every collection grid on the page.
 */
function setupWrapper(wrapper) {
  if (wrapper.dataset.viewControlsBound === 'true') return;

  const grids = getCollectionPageGrids();
  if (grids.length === 0) return;

  wrapper.dataset.viewControlsBound = 'true';

  const mobileLabel = wrapper.querySelector('[data-view-by-label-mobile]');
  const desktopLabel = wrapper.querySelector('[data-view-by-label-desktop]');
  const triggers = wrapper.querySelectorAll('[data-view-by-trigger]');

  function primaryGrid() {
    return grids[0];
  }

  function setGridLayoutAll(value) {
    grids.forEach((grid) => grid.setAttribute('product-grid-view', value));
  }

  function applyLayoutAll(value, animate = false) {
    if (animate) {
      startViewTransition(() => setGridLayoutAll(value), ['product-grid']);
    } else {
      setGridLayoutAll(value);
    }
  }

  function syncFromStorage() {
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const stored = getStoredLayout(viewport);

    if (MOBILE_VIEWS.includes(stored) || DESKTOP_VIEWS.includes(stored)) {
      setGridLayoutAll(stored);
      if (viewport === 'mobile' && mobileLabel) {
        updateLabel(mobileLabel, getLabelToDisplay(stored, 'mobile'));
      }
      if (viewport === 'desktop' && desktopLabel) {
        updateLabel(desktopLabel, getLabelToDisplay(stored, 'desktop'));
      }
    }
  }

  function handleClick() {
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const currentView =
      primaryGrid().getAttribute('product-grid-view') ||
      (viewport === 'desktop' ? 'view-by-6' : 'view-by-2');
    const nextView = toggleView(currentView, viewport);

    applyLayoutAll(nextView, true);

    /* Label shows what you'll get on next click (the target). */
    if (mobileLabel) {
      const mobileView = mediaQueryLarge.matches ? getStoredLayout('mobile') : nextView;
      updateLabel(mobileLabel, getLabelToDisplay(mobileView, 'mobile'));
    }
    if (desktopLabel) {
      const desktopView = mediaQueryLarge.matches ? nextView : getStoredLayout('desktop');
      updateLabel(desktopLabel, getLabelToDisplay(desktopView, 'desktop'));
    }

    requestIdleCallback(() => saveLayout(viewport, nextView));
  }

  triggers.forEach((btn) => {
    btn.addEventListener('click', handleClick);
  });

  mediaQueryLarge.addEventListener('change', () => {
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const stored = getStoredLayout(viewport);
    applyLayoutAll(stored);
    if (mobileLabel) {
      const mobileView = mediaQueryLarge.matches ? getStoredLayout('mobile') : stored;
      updateLabel(mobileLabel, getLabelToDisplay(mobileView, 'mobile'));
    }
    if (desktopLabel) {
      const desktopView = mediaQueryLarge.matches ? stored : getStoredLayout('desktop');
      updateLabel(desktopLabel, getLabelToDisplay(desktopView, 'desktop'));
    }
  });

  syncFromStorage();
}

function init() {
  document.querySelectorAll('[data-collection-view-controls]').forEach(setupWrapper);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
